import {useCallback, useState} from 'react';
import axios from 'axios';
import Environment from '../../../../../environment';

// ── useGetArenaPlayerGames / fetchArenaGameReplay ────────────────────────────
// Per-player arena game HISTORY + the redis-first replay source, from the game
// service's REST surface (auth + base-URL derivation mirror useGetArenaGames):
//   GET {GAME_SERVICE_URL}/arenaPlayerGames?arenaId=&userId=
//     → {code:200, games:[ArenaPlayerGame...]}  (oldest→newest, that player's POV)
//   GET {GAME_SERVICE_URL}/arenaGameReplay?tableId=
//     → {code:200, found:true, ...watch-shaped snapshot} while redis still holds
//        the table (7-day TTL), else {found:false} → caller falls back to the
//        DB-uuid replay. Redis presence IS the gate (no brittle date check).
// GAME_SERVICE_URL is the socket origin (ws(s)://); REST rides the same host
// over http(s), hence the scheme swap.

const GAME_SERVICE_REST_BASE = String(
  Environment.GAME_SERVICE_URL || '',
).replace(/^ws/, 'http');

export interface ArenaPlayerGame {
  seq: number;
  tableId: string;
  color: 'white' | 'black';
  oppId: string;
  oppName: string;
  oppRating: number;
  outcome: 'win' | 'loss' | 'draw';
  result: string; // '1-0' | '0-1' | '0.5-0.5'
  points: number;
  berserk: boolean;
  oppBerserk: boolean;
  moveCount: number;
  endType: string;
  gameUuid: string;
  endedAt: string;
}

// Redis-fresh replay snapshot for a (recently) finished arena game. Returns the
// raw response so the caller can branch on `found`.
export async function fetchArenaGameReplay(
  tableId: string | number,
): Promise<any> {
  if (!tableId || !GAME_SERVICE_REST_BASE) {
    return {found: false};
  }
  const res = await axios.get(`${GAME_SERVICE_REST_BASE}/arenaGameReplay`, {
    params: {tableId: String(tableId)},
    headers: {'x-api-key': Environment.X_API_KEY},
  });
  return res?.data || {found: false};
}

// Fetch-on-demand hook: `fetchPlayerGames(arenaId, uid)` loads that player's
// full arena history. `forUid` records whose list is currently held so callers
// can toggle an expanded panel per player without re-fetching the wrong list.
export default function useGetArenaPlayerGames() {
  const [games, setGames] = useState<ArenaPlayerGame[]>([]);
  const [loading, setLoading] = useState(false);
  const [forUid, setForUid] = useState<string>('');

  const fetchPlayerGames = useCallback(
    async (arenaId?: string | number, userId?: string | number) => {
      if (!arenaId || !userId || !GAME_SERVICE_REST_BASE) {
        return;
      }
      setLoading(true);
      setForUid(String(userId));
      try {
        const res = await axios.get(
          `${GAME_SERVICE_REST_BASE}/arenaPlayerGames`,
          {
            params: {arenaId: String(arenaId), userId: String(userId)},
            headers: {'x-api-key': Environment.X_API_KEY},
          },
        );
        if (res?.data?.code === 200 && Array.isArray(res.data.games)) {
          setGames(res.data.games);
        } else {
          setGames([]);
        }
      } catch (e: any) {
        console.warn('arenaPlayerGames fetch failed:', e?.message);
        setGames([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clear = useCallback(() => {
    setGames([]);
    setForUid('');
  }, []);

  return {games, loading, forUid, fetchPlayerGames, clear};
}
